import { supabase } from "../lib/supabase";

const API_BASE = process.env.REACT_APP_API_BASE || "";

/** Server caches per day; short client TTL just avoids duplicate mounts refetching. */
const BRIEFING_CACHE_TTL_MS = 5 * 60_000;

// Per-kind cache + in-flight dedupe ("morning" | "evening").
const caches = Object.create(null); // kind -> { key, data, expiresAt }
const inflights = Object.create(null); // kind -> { promise, key }

async function currentAccessToken() {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch {
    return null;
  }
}

async function fetchBriefingUncached(token, kind) {
  const query = kind === "evening" ? "?kind=evening" : "";
  const res = await fetch(`${API_BASE}/api/briefing${query}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Briefing konnte nicht geladen werden.");
  return res.json();
}

async function fetchBriefing(kind) {
  const token = await currentAccessToken();
  // Cache key separates anonymous from each signed-in session.
  const key = token ? `u:${token.slice(-16)}` : "anon";
  const now = Date.now();

  const cache = caches[kind];
  if (cache && cache.key === key && cache.expiresAt > now) return cache.data;

  const inflight = inflights[kind];
  if (!inflight || inflight.key !== key) {
    const promise = fetchBriefingUncached(token, kind)
      .then((data) => {
        caches[kind] = { key, data, expiresAt: Date.now() + BRIEFING_CACHE_TTL_MS };
        return data;
      })
      .finally(() => {
        if (inflights[kind]?.promise === promise) inflights[kind] = null;
      });
    inflights[kind] = { promise, key };
  }
  return inflights[kind].promise;
}

/**
 * Today's Morning Briefing. Personalized for the logged-in user when their
 * family profile is set; cached briefly per auth state to avoid refetch churn.
 * @returns {Promise<{ title: string, body: string, url: string, chatText?: string, sections: Array<{ id: string, label: string, text: string }> }>}
 */
export function fetchMorningBriefing() {
  return fetchBriefing("morning");
}

/**
 * Tonight's Evening Decompression — wind-down, tomorrow's weather when it
 * shifted, and a self-care nudge. Same caching/auth as the Morning Briefing.
 * @returns {Promise<{ title: string, body: string, url: string, chatText?: string, sections: Array<{ id: string, label: string, text: string }> }>}
 */
export function fetchEveningDecompression() {
  return fetchBriefing("evening");
}
